// Context for managing external site links (watch for anime, read for manga)

import React, { createContext, useContext, useState, useEffect } from 'react';
import { getWatchLinksFromServer, saveWatchLinksToServer, getSettingFromServer, saveSettingToServer } from '../services/apiService';
import { waitForServerAuth } from '../services/authReadyService';

const LinkSettingsContext = createContext();

const STORAGE_KEY_WATCH = 'external_watch_links';
const STORAGE_KEY_READ = 'external_read_links';
const DB_KEY_WATCH = 'watch_links';
const DB_KEY_READ = 'read_links';

// Check if user is connected (via OAuth token)
const isUserConnected = () => {
    const { hasToken } = require('../services/tokenStorage');
    return hasToken();
};

// Default links
const DEFAULT_WATCH_LINKS = [
    { id: 'crunchyroll', name: 'Crunchyroll', baseUrl: 'https://www.crunchyroll.com/search?q=' }
];

const DEFAULT_READ_LINKS = [
    { id: 'mangaplus', name: 'MANGA Plus', baseUrl: 'https://mangaplus.shueisha.co.jp/search_result?keyword=' }
];

// Helper to save links to localStorage
const saveToLocalStorage = (key, data) => {
    try {
        localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
        console.error(`Error saving ${key} to localStorage:`, error);
    }
};

// Helper to save links to database
const saveToDatabase = async (key, data) => {
    try {
        const sqliteService = (await import('../services/sqliteService')).default;
        await sqliteService.saveUserSetting(key, data);
    } catch (error) {
        console.error(`Error saving ${key} to database:`, error);
    }
};

// Helper to persist watch links
const persistWatchLinks = (data) => {
    saveToLocalStorage(STORAGE_KEY_WATCH, data);
    saveToDatabase(DB_KEY_WATCH, data);
    if (isUserConnected()) {
        saveWatchLinksToServer(data).catch(err =>
            console.warn('Server sync failed for watch links:', err.message)
        );
    }
};

// Helper to persist read links
const persistReadLinks = (data) => {
    saveToLocalStorage(STORAGE_KEY_READ, data);
    saveToDatabase(DB_KEY_READ, data);
    if (isUserConnected()) {
        saveSettingToServer('read_links', data).catch(err =>
            console.warn('Server sync failed for read links:', err.message)
        );
    }
};

export const LinkSettingsProvider = ({ children }) => {
    // Initialize watch links from localStorage
    const [watchLinks, setWatchLinks] = useState(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY_WATCH);
            return stored ? JSON.parse(stored) : DEFAULT_WATCH_LINKS;
        } catch (error) {
            return DEFAULT_WATCH_LINKS;
        }
    });

    // Initialize read links from localStorage
    const [readLinks, setReadLinks] = useState(() => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY_READ);
            return stored ? JSON.parse(stored) : DEFAULT_READ_LINKS;
        } catch (error) {
            return DEFAULT_READ_LINKS;
        }
    });

    // On mount: load watch links from server/database
    useEffect(() => {
        const loadWatchLinks = async () => {
            try {
                if (isUserConnected()) {
                    await waitForServerAuth(); // Wait for server login to complete
                    const serverLinks = await getWatchLinksFromServer();
                    if (serverLinks && Array.isArray(serverLinks) && serverLinks.length > 0) {
                        console.log('📡 Restored watch links from server');
                        setWatchLinks(serverLinks);
                        saveToLocalStorage(STORAGE_KEY_WATCH, serverLinks);
                        saveToDatabase(DB_KEY_WATCH, serverLinks);
                        return;
                    }
                }
                const sqliteService = (await import('../services/sqliteService')).default;
                const dbLinks = await sqliteService.getUserSetting(DB_KEY_WATCH);
                if (dbLinks && Array.isArray(dbLinks) && dbLinks.length > 0) {
                    const localStored = localStorage.getItem(STORAGE_KEY_WATCH);
                    if (!localStored) {
                        console.log('📦 Restored watch links from database');
                        setWatchLinks(dbLinks);
                        saveToLocalStorage(STORAGE_KEY_WATCH, dbLinks);
                    }
                }
            } catch (error) {
                console.error('Error loading watch links:', error);
            }
        };

        const loadReadLinks = async () => {
            try {
                if (isUserConnected()) {
                    await waitForServerAuth(); // Wait for server login to complete
                    const serverLinks = await getSettingFromServer('read_links');
                    if (serverLinks && Array.isArray(serverLinks) && serverLinks.length > 0) {
                        console.log('📡 Restored read links from server');
                        setReadLinks(serverLinks);
                        saveToLocalStorage(STORAGE_KEY_READ, serverLinks);
                        saveToDatabase(DB_KEY_READ, serverLinks);
                        return;
                    }
                }
                const sqliteService = (await import('../services/sqliteService')).default;
                const dbLinks = await sqliteService.getUserSetting(DB_KEY_READ);
                if (dbLinks && Array.isArray(dbLinks) && dbLinks.length > 0) {
                    const localStored = localStorage.getItem(STORAGE_KEY_READ);
                    if (!localStored) {
                        console.log('📦 Restored read links from database');
                        setReadLinks(dbLinks);
                        saveToLocalStorage(STORAGE_KEY_READ, dbLinks);
                    }
                }
            } catch (error) {
                console.error('Error loading read links:', error);
            }
        };

        loadWatchLinks();
        loadReadLinks();
    }, []);

    // Process URL: extract everything up to and including the '=' sign
    const processUrl = (url) => {
        const trimmedUrl = url.trim();
        const equalsIndex = trimmedUrl.lastIndexOf('=');
        if (equalsIndex !== -1) {
            return trimmedUrl.substring(0, equalsIndex + 1);
        }
        return trimmedUrl;
    };

    // ========== Watch Link CRUD ==========
    const addWatchLink = (name, url) => {
        if (!name || !url) return false;
        const baseUrl = processUrl(url);
        const newLink = { id: `watch_${Date.now()}`, name: name.trim(), baseUrl };
        setWatchLinks(prev => {
            const updated = [...prev, newLink];
            persistWatchLinks(updated);
            return updated;
        });
        return true;
    };

    const removeWatchLink = (id) => {
        setWatchLinks(prev => {
            const updated = prev.filter(link => link.id !== id);
            persistWatchLinks(updated);
            return updated;
        });
    };

    const moveWatchUp = (id) => {
        setWatchLinks(prev => {
            const index = prev.findIndex(link => link.id === id);
            if (index > 0) {
                const updated = [...prev];
                [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
                persistWatchLinks(updated);
                return updated;
            }
            return prev;
        });
    };

    const moveWatchDown = (id) => {
        setWatchLinks(prev => {
            const index = prev.findIndex(link => link.id === id);
            if (index < prev.length - 1) {
                const updated = [...prev];
                [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
                persistWatchLinks(updated);
                return updated;
            }
            return prev;
        });
    };

    // ========== Read Link CRUD ==========
    const addReadLink = (name, url) => {
        if (!name || !url) return false;
        const baseUrl = processUrl(url);
        const newLink = { id: `read_${Date.now()}`, name: name.trim(), baseUrl };
        setReadLinks(prev => {
            const updated = [...prev, newLink];
            persistReadLinks(updated);
            return updated;
        });
        return true;
    };

    const removeReadLink = (id) => {
        setReadLinks(prev => {
            const updated = prev.filter(link => link.id !== id);
            persistReadLinks(updated);
            return updated;
        });
    };

    const moveReadUp = (id) => {
        setReadLinks(prev => {
            const index = prev.findIndex(link => link.id === id);
            if (index > 0) {
                const updated = [...prev];
                [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
                persistReadLinks(updated);
                return updated;
            }
            return prev;
        });
    };

    const moveReadDown = (id) => {
        setReadLinks(prev => {
            const index = prev.findIndex(link => link.id === id);
            if (index < prev.length - 1) {
                const updated = [...prev];
                [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
                persistReadLinks(updated);
                return updated;
            }
            return prev;
        });
    };

    return (
        <LinkSettingsContext.Provider value={{
            watchLinks,
            addWatchLink,
            removeWatchLink,
            moveWatchUp,
            moveWatchDown,
            readLinks,
            addReadLink,
            removeReadLink,
            moveReadUp,
            moveReadDown
        }}>
            {children}
        </LinkSettingsContext.Provider>
    );
};

export const useLinkSettings = () => {
    const context = useContext(LinkSettingsContext);
    if (!context) {
        throw new Error('useLinkSettings must be used within LinkSettingsProvider');
    }
    return context;
};
